/**
 * BrightPath Family Store
 * 
 * Zustand store for managing family-related state
 * Persists selected family across sessions
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Family, Child } from '@/features/family/types/familyTypes';

// =============================================================================
// Store Interface
// =============================================================================

interface FamilyState {
  // Current family context
  currentFamilyId: string | null;
  currentFamily: Family | null;
  
  // Cached children for quick access
  children: Child[];
  
  // Loading states
  isLoading: boolean;
  isInitialized: boolean;
  
  // Actions
  setCurrentFamily: (family: Family | null) => void;
  setCurrentFamilyId: (familyId: string | null) => void;
  setChildren: (children: Child[]) => void;
  addChild: (child: Child) => void;
  updateChild: (childId: string, updates: Partial<Child>) => void;
  removeChild: (childId: string) => void;
  setLoading: (isLoading: boolean) => void;
  setInitialized: (isInitialized: boolean) => void;
  clearFamily: () => void;
  reset: () => void;
}

// =============================================================================
// Initial State
// =============================================================================

const initialState = {
  currentFamilyId: null,
  currentFamily: null,
  children: [],
  isLoading: false,
  isInitialized: false,
};

// =============================================================================
// Store Implementation
// =============================================================================

export const useFamilyStore = create<FamilyState>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Set the current family and update the ID
       */
      setCurrentFamily: (family) => {
        set({
          currentFamily: family,
          currentFamilyId: family?.id ?? null,
        });
      },

      /**
       * Set just the family ID (useful when loading)
       */
      setCurrentFamilyId: (familyId) => {
        set({ currentFamilyId: familyId });
      },

      /**
       * Set the children list for the current family
       */
      setChildren: (children) => {
        set({ children });
      },

      /**
       * Add a new child to the list
       */
      addChild: (child) => {
        const { children } = get();
        set({ children: [...children, child] });
      },

      /**
       * Update an existing child
       */
      updateChild: (childId, updates) => {
        const { children } = get();
        set({
          children: children.map((child) =>
            child.id === childId ? { ...child, ...updates } : child
          ),
        });
      },

      /**
       * Remove a child from the list
       */
      removeChild: (childId) => {
        const { children } = get();
        set({
          children: children.filter((child) => child.id !== childId),
        });
      },

      /**
       * Set loading state
       */
      setLoading: (isLoading) => {
        set({ isLoading });
      },

      /**
       * Set initialized state (after initial data fetch)
       */
      setInitialized: (isInitialized) => {
        set({ isInitialized });
      },

      /**
       * Clear family context (when switching families)
       */
      clearFamily: () => {
        set({
          currentFamily: null,
          children: [],
        });
      },

      /**
       * Full reset (on logout)
       */
      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'brightpath-family',
      storage: createJSONStorage(() => localStorage),
      // Only persist the family ID, not the full data
      partialize: (state) => ({
        currentFamilyId: state.currentFamilyId,
      }),
    }
  )
);

// =============================================================================
// Selector Hooks
// =============================================================================

/**
 * Get the current family ID
 */
export const useCurrentFamilyId = () => 
  useFamilyStore((state) => state.currentFamilyId);

/**
 * Get the current family object
 */
export const useCurrentFamily = () => 
  useFamilyStore((state) => state.currentFamily);

/**
 * Get the children list
 */
export const useFamilyChildren = () => 
  useFamilyStore((state) => state.children);

/**
 * Get a specific child by ID
 */
export const useChildById = (childId: string | undefined) =>
  useFamilyStore((state) => 
    childId ? state.children.find((c) => c.id === childId) : undefined
  );

/**
 * Check if family context is loaded
 */
export const useFamilyIsReady = () =>
  useFamilyStore((state) => state.isInitialized && !state.isLoading);

// =============================================================================
// Action Hooks (for use outside of components)
// =============================================================================

export const familyStoreActions = {
  setCurrentFamily: useFamilyStore.getState().setCurrentFamily,
  setCurrentFamilyId: useFamilyStore.getState().setCurrentFamilyId,
  setChildren: useFamilyStore.getState().setChildren,
  addChild: useFamilyStore.getState().addChild,
  updateChild: useFamilyStore.getState().updateChild,
  removeChild: useFamilyStore.getState().removeChild,
  setLoading: useFamilyStore.getState().setLoading,
  setInitialized: useFamilyStore.getState().setInitialized,
  clearFamily: useFamilyStore.getState().clearFamily,
  reset: useFamilyStore.getState().reset,
};

